/** Client mirror of server/constants/fieldTypesRegistry.js */

export type FieldCategory =
  | 'text' | 'numeric' | 'datetime' | 'selection' | 'boolean'
  | 'file' | 'relation' | 'system' | 'advanced';

export interface FieldTypeDef {
  key: string;
  label: string;
  category: FieldCategory;
  dbMapping: string;
  uiComponent: string;
  description: string;
  enabled?: boolean;
  system?: boolean;
  aliasOf?: string;
}

export interface ModuleFieldDef {
  key: string;
  label: string;
  type: string;
  required?: boolean;
  unique?: boolean;
  defaultValue?: unknown;
  readOnly?: boolean;
  hidden?: boolean;
  searchable?: boolean;
  filterable?: boolean;
  sortable?: boolean;
  options?: string[];
  relationModule?: string;
  relationType?: 'one' | 'many';
  currencyCode?: string;
  sortOrder?: number;
  visible?: boolean;
  showInList?: boolean;
  width?: string;
  fieldState?: 'active' | 'hidden' | 'disabled' | 'deleted';
}

export const FIELD_TYPE_CATEGORIES: Record<FieldCategory, string> = {
  text: 'Text-Based',
  numeric: 'Numeric',
  datetime: 'Date & Time',
  selection: 'Enum / Selection',
  boolean: 'Boolean & Status',
  file: 'File & Media',
  relation: 'Relational',
  system: 'System & Auto',
  advanced: 'Advanced',
};

export const FIELD_PROPERTIES = [
  'required', 'unique', 'defaultValue', 'readOnly', 'hidden',
  'searchable', 'filterable', 'sortable',
] as const;

export const FIELD_TYPES_REGISTRY: FieldTypeDef[] = [
  { key: 'text', label: 'Short Text', category: 'text', dbMapping: 'varchar(255)', uiComponent: 'input', description: 'Single line text' },
  { key: 'textarea', label: 'Long Text', category: 'text', dbMapping: 'text', uiComponent: 'textarea', description: 'Multi-line text' },
  { key: 'richtext', label: 'Rich Text', category: 'text', dbMapping: 'text/json', uiComponent: 'richtext', description: 'HTML editor' },
  { key: 'email', label: 'Email', category: 'text', dbMapping: 'varchar', uiComponent: 'email', description: 'Email validation' },
  { key: 'phone', label: 'Phone', category: 'text', dbMapping: 'varchar', uiComponent: 'phone', description: 'Phone format' },
  { key: 'url', label: 'URL', category: 'text', dbMapping: 'varchar', uiComponent: 'url', description: 'Website link' },
  { key: 'integer', label: 'Integer', category: 'numeric', dbMapping: 'int', uiComponent: 'number', description: 'Whole numbers' },
  { key: 'decimal', label: 'Decimal', category: 'numeric', dbMapping: 'decimal(10,2)', uiComponent: 'decimal', description: 'Money, rates' },
  { key: 'float', label: 'Float', category: 'numeric', dbMapping: 'float', uiComponent: 'number', description: 'Approx numbers' },
  { key: 'percentage', label: 'Percentage', category: 'numeric', dbMapping: 'decimal', uiComponent: 'percentage', description: '0–100%' },
  { key: 'currency', label: 'Currency', category: 'numeric', dbMapping: 'decimal+currencyCode', uiComponent: 'currency', description: 'Money field' },
  { key: 'number', label: 'Number', category: 'numeric', dbMapping: 'number', uiComponent: 'number', description: 'Generic number' },
  { key: 'date', label: 'Date', category: 'datetime', dbMapping: 'date', uiComponent: 'date', description: 'Date only' },
  { key: 'datetime', label: 'DateTime', category: 'datetime', dbMapping: 'datetime', uiComponent: 'datetime', description: 'Date + time' },
  { key: 'time', label: 'Time', category: 'datetime', dbMapping: 'time', uiComponent: 'time', description: 'Time only' },
  { key: 'duration', label: 'Duration', category: 'datetime', dbMapping: 'int', uiComponent: 'duration', description: 'Minutes/hours' },
  { key: 'enum', label: 'Dropdown', category: 'selection', dbMapping: 'enum/string', uiComponent: 'select', description: 'Single select' },
  { key: 'multienum', label: 'Multi-select', category: 'selection', dbMapping: 'array/json', uiComponent: 'multiselect', description: 'Multiple values' },
  { key: 'radio', label: 'Radio', category: 'selection', dbMapping: 'enum', uiComponent: 'radio', description: 'Radio buttons' },
  { key: 'checkboxgroup', label: 'Checkbox Group', category: 'selection', dbMapping: 'array', uiComponent: 'checkboxgroup', description: 'Multi checkbox' },
  { key: 'dropdown', label: 'Dropdown', category: 'selection', dbMapping: 'enum/string', uiComponent: 'select', description: 'Legacy alias', aliasOf: 'enum' },
  { key: 'multiselect', label: 'Multi Select', category: 'selection', dbMapping: 'array/json', uiComponent: 'multiselect', description: 'Legacy alias', aliasOf: 'multienum' },
  { key: 'boolean', label: 'Boolean', category: 'boolean', dbMapping: 'boolean', uiComponent: 'checkbox', description: 'Yes/No' },
  { key: 'status', label: 'Status', category: 'boolean', dbMapping: 'enum', uiComponent: 'status', description: 'Workflow states' },
  { key: 'file', label: 'File', category: 'file', dbMapping: 'filePath', uiComponent: 'file', description: 'File upload' },
  { key: 'image', label: 'Image', category: 'file', dbMapping: 'filePath', uiComponent: 'image', description: 'Image only' },
  { key: 'attachment', label: 'Attachment', category: 'file', dbMapping: 'array', uiComponent: 'attachment', description: 'Multiple files' },
  { key: 'relation', label: 'Lookup', category: 'relation', dbMapping: 'ObjectId', uiComponent: 'relation', description: 'One-to-one' },
  { key: 'user', label: 'User', category: 'relation', dbMapping: 'ObjectId', uiComponent: 'user', description: 'Assign to user' },
  { key: 'multirelation', label: 'Multi Lookup', category: 'relation', dbMapping: 'array<ObjectId>', uiComponent: 'multirelation', description: 'One-to-many' },
  { key: 'json', label: 'JSON', category: 'advanced', dbMapping: 'json', uiComponent: 'json', description: 'Structured data' },
  { key: 'barcode', label: 'Barcode', category: 'advanced', dbMapping: 'string', uiComponent: 'barcode', description: 'Scan support' },
  { key: 'location', label: 'Location', category: 'advanced', dbMapping: 'geo', uiComponent: 'location', description: 'Lat/Long' },
  { key: 'rating', label: 'Rating', category: 'advanced', dbMapping: 'int', uiComponent: 'rating', description: '1–5 stars' },
];

export const TYPE_ALIASES: Record<string, string> = {
  dropdown: 'enum',
  multiselect: 'multienum',
  number: 'integer',
};

export const normalizeFieldType = (type?: string) =>
  (type && TYPE_ALIASES[type]) || type || 'text';

export const getFieldTypeLabel = (key: string) =>
  FIELD_TYPES_REGISTRY.find((t) => t.key === key)?.label || key;

export const groupFieldTypesByCategory = (types: FieldTypeDef[]) => {
  const grouped: Record<string, FieldTypeDef[]> = {};
  types.forEach((t) => {
    if (t.system) return;
    const cat = FIELD_TYPE_CATEGORIES[t.category] || t.category;
    if (!grouped[cat]) grouped[cat] = [];
    grouped[cat].push(t);
  });
  return grouped;
};

/** Fallback when API unavailable */
export const FIELD_TYPE_OPTIONS = FIELD_TYPES_REGISTRY
  .filter((t) => !t.system && !t.aliasOf)
  .map((t) => ({ value: t.key, label: t.label, category: t.category }));
